import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMinus, FaPlus, FaTrophy, FaTrash } from 'react-icons/fa6';
import classNames from 'classnames';
import { FaEdit } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Transition } from '@headlessui/react';
import { format } from 'date-fns';
import Button from '../../components/buttons/Button';
import ScoreCardModal from '../../components/modals/ScoreCardModal';
import {
  addMemberToScoreCard,
  createScoreCard,
  deleteScoreCard,
  getAllScoreCards,
  removeMemberFromScoreCard,
  updateScoreCard,
  updateScoreCardScores,
} from '../../../services/community/listService';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import Labeled from '../../components/misc/Labeled';
import TabButtons from '../../components/buttons/TabButtons';
import { ConnectionsListConnection } from '../../../types/misc';
import Toggle from '../../components/misc/Toggle';
import ConnectionsList from '../../components/misc/ConnectionsList';
import PageHeader from '../../components/misc/PageHeader';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import Avatar from '../../components/misc/Avatar';
import { GetAllScoreCardsResponse, ScoreCard, ScoreCardScoreModel, ScoreCardType } from '../../../models/scoreCard.model';
import { formatScore, parseScore } from '../../../utils/scoreCardUtil';

export default function ScoreListsPage(): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.lists.score' });
  const { t: errorT } = useTranslation('', { keyPrefix: 'page.lists.score.error' });
  const [selectedCard, setSelectedCard] = useState<ScoreCard>();
  const [cards, setCards] = useState<ScoreCard[]>([]);
  const [membersOfCommunity, setMembersOfCommunity] = useState<ConnectionsListConnection[]>([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<ScoreCard | null>(null);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const selectedCommunity = useSelectedCommunity();

  useEffect(() => {
    getAllScoreCards()
      .then((data: GetAllScoreCardsResponse) => {
        setCards(data || []);
        setMembersOfCommunity([]);
        if (data && data.length > 0 && !selectedCard) {
          setSelectedCard(data[0]);
        }
      })
      .catch(() => {
        toast.error(errorT('fetchError'));
        setCards([]);
      });
  }, [selectedCommunity]);

  useEffect(() => {
    if (cards && cards.length > 0) {
      const updatedCard = cards.find((card) => card.scoreCardId === selectedCard?.scoreCardId);
      if (updatedCard) {
        setSelectedCard(updatedCard);
      } else if (cards.length > 0) {
        setSelectedCard(cards[0]);
      } else {
        setSelectedCard(undefined);
      }
    }
  }, [cards]);

  if (!selectedCommunity) return <></>;

  const handleEditCard = (submittedCard: { name: string; ascending: boolean; description?: string }) => {
    if (!editingCard) return Promise.reject(new Error('STOP'));
    if (!submittedCard.name || !submittedCard.name.trim()) return Promise.reject(new Error('STOP'));

    return updateScoreCard(editingCard.scoreCardId, { ...submittedCard, selectedUserIds: undefined })
      .then((newCard) => {
        const editedCard: ScoreCard = {
          ...editingCard,
          name: submittedCard.name,
          description: submittedCard.description,
          ascending: submittedCard.ascending,
          lastUpdated: new Date(),
        };
        setCards((prev) => prev.map((c) => (c.scoreCardId === editingCard.scoreCardId ? editedCard : c)));
      })
      .catch((err: Error) => {
        toast.error(err.message === 'UNAUTHORIZED' ? errorT('unauthorizedError') : errorT('editError'));
      });
  };

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        primaryButton={
          cards.length > 0 ? (
            <PrimaryButton
              text={t('createButton')}
              icon={FaPlus}
              onClick={() => setCreateModalOpen(true)}
            />
          ) : undefined
        }
      />

      <main className="relative flex flex-col gap-6 md:flex-row">
        {cards.length === 0 ? (
          <div className="flex w-full flex-col items-center justify-center gap-4 py-12">
            <FaTrophy className="mb-4 h-16 w-16 text-primary-300" />
            <EmptyScreenView title={t('noCardsTitle')} subtitle={t('noCardsSubtitle')} />
            <PrimaryButton
              text={t('createButton')}
              icon={FaPlus}
              onClick={() => setCreateModalOpen(true)}
            />
          </div>
        ) : (
          <>
            {/* Sidebar */}
            <div className="w-full flex-shrink-0 md:w-80">
              <div className="overflow-hidden rounded-[12px] border border-secondary-200 bg-surface">
                <div className="bg-primary-100 px-4 py-2.5">
                  <h3 className="text-sm font-semibold text-primary-900">{t('scoreCards')}</h3>
                </div>
                <div className="flex flex-col gap-2 p-3">
                  {cards.map((card) => (
                    <div
                      key={card.scoreCardId}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedCard(card)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setSelectedCard(card);
                      }}
                      className={classNames(
                        'flex cursor-pointer items-center gap-3 rounded-[8px] bg-secondary-200/70 px-3 py-3 text-left transition-colors hover:bg-secondary-200',
                        {
                          'ring-2 ring-primary-100 bg-primary-100/50':
                            selectedCard?.scoreCardId === card.scoreCardId,
                        },
                      )}>
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[8px] bg-primary-100 text-primary-900">
                        <FaTrophy className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-primary-900">{card.name}</p>
                        <p className="text-xs text-gray-500">
                          {card.scores.length} {t('members')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCard(card);
                          setEditPanelOpen(true);
                        }}
                        aria-label={t('forms.editTitle')}
                        className="ml-2 rounded-[8px] bg-surface p-2 text-primary-900 hover:bg-secondary-200">
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1">
              {selectedCard ? (
                <ScoreCardSection
                  card={selectedCard}
                  membersOfCommunity={membersOfCommunity}
                  onUpdateCard={(newCard) => {
                    setCards((prev) =>
                      prev.map((c) => (c.scoreCardId === newCard.scoreCardId ? newCard : c)),
                    );
                  }}
                  onDeleteCard={(scoreCardId) => {
                    setCards((prev) => prev.filter((c) => c.scoreCardId !== scoreCardId));
                  }}
                />
              ) : (
                <SelectCardPrompt />
              )}
            </div>
          </>
        )}
      </main>

      {/* Create modal */}
      <ScoreCardModal
        open={createModalOpen}
        setOpen={setCreateModalOpen}
        onSubmit={(card) => {
          if (!card.name || card.name.trim() === '') return Promise.reject(new Error('STOP'));
          return createScoreCard({
            ...card,
            containsAllMembers: card.containsAllMembers ?? true,
            communityId: selectedCommunity?.id!,
            ascending: card.ascending ?? false,
          })
            .then((newCard) => {
              setCards((prev) => [...prev, newCard]);
              setSelectedCard(newCard);
            })
            .catch((err: Error) =>
              toast.error(
                err.message === 'UNAUTHORIZED' ? errorT('unauthorizedError') : errorT('createError'),
              ),
            )
            .then(() => {});

        }}
        membersOfCommunity={membersOfCommunity}
      />

      {/* Edit card metadata panel */}
      <Transition
        as={React.Fragment}
        show={editPanelOpen && !!editingCard}
        enter="transition-all duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-all duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0">
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-30">
          <div className="z-50 flex w-full max-w-lg flex-col gap-3 rounded-xl bg-secondary-50 p-6 shadow-xl">
            <h4 className="text-lg font-semibold">{t('forms.editTitle')}</h4>
            {editingCard && (
              <ScoreCardEditForm
                onEdit={handleEditCard}
                initialState={editingCard}
                setEditModalOpen={(open) => {
                  setEditPanelOpen(open);
                  if (!open) setEditingCard(null);
                }}
                membersOfCommunity={membersOfCommunity}
                setCard={(updated) => {
                  setCards((prev) =>
                    prev.map((c) => (c.scoreCardId === updated.scoreCardId ? updated : c)),
                  );
                }}
              />
            )}
          </div>
        </div>
      </Transition>
    </>
  );
}

function ScoreCardSection({
  card,
  membersOfCommunity,
  onUpdateCard,
  onDeleteCard,
}: {
  card: ScoreCard;
  membersOfCommunity: ConnectionsListConnection[];
  onUpdateCard: (newCard: ScoreCard) => void;
  onDeleteCard: (scoreCardId: number) => void;
}): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.lists.score' });
  const { t: errorT } = useTranslation('', { keyPrefix: 'page.lists.score.error' });
  const [editMode, setEditMode] = useState(false);
  const [pendingScores, setPendingScores] = useState<Record<number, number>>({});
  const [saving, setSaving] = useState(false);
  const [cancelVersion, setCancelVersion] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const sortedScores = [...card.scores].sort((a, b) =>
    card.ascending ? a.score - b.score : b.score - a.score,
  );
  const topThree = sortedScores.slice(0, 3);

  const handleSave = async () => {
    if (Object.keys(pendingScores).length === 0) {
      setEditMode(false);
      return;
    }
    setSaving(true);
    try {
      const scores = Object.entries(pendingScores).map(([memberId, score]) => ({
        memberId: Number(memberId),
        score: Number(score),
      }));
      await updateScoreCardScores(card.scoreCardId, scores);

      const refreshed = await getAllScoreCards();
      const updatedCard = refreshed.find((c) => c.scoreCardId === card.scoreCardId);
      if (updatedCard) {
        onUpdateCard(updatedCard);
        setPendingScores({});
      }
    } catch (err: any) {
      toast.error(
        err.message === 'UNAUTHORIZED' ? errorT('unauthorizedError') : errorT('updateScoreError'),
      );
    } finally {
      setSaving(false);
      setEditMode(false);
    }
  };

  const handleDelete = () => {
    deleteScoreCard(card.scoreCardId)
      .then(() => {
        setDeleteConfirmOpen(false);
        onDeleteCard(card.scoreCardId);
      })
      .catch((err: Error) => {
        toast.error(
          err.message === 'UNAUTHORIZED' ? errorT('unauthorizedError') : errorT('deleteError'),
        );
      });
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Podium */}
      {topThree.length >= 3 && (
        <div className="flex flex-row items-end justify-center gap-4">
          <PodiumPlace score={topThree[1]} place={2} size="medium" card={card} />
          <PodiumPlace score={topThree[0]} place={1} size="large" isFirst card={card} />
          <PodiumPlace score={topThree[2]} place={3} size="small" card={card} />
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-[12px] border border-secondary-200 bg-surface">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-primary-100 px-4 py-3 md:px-6">
          <div>
            <h2 className="text-lg font-semibold text-primary-900">{card.name}</h2>
            {!!card.description && (
              <p className="text-sm text-gray-600">{card.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!editMode ? (
              <>
                <Button
                  variant="primary"
                  onClick={() => setEditMode(true)}
                  className="gap-2">
                  {t('editScores')}
                  <FiEdit2 className="h-4 w-4" />
                </Button>
                <div className="relative">
                  <Button
                    variant="danger-v2"
                    onClick={() => setDeleteConfirmOpen((v) => !v)}
                    className="py-3">
                    <FaTrash />
                  </Button>
                  <Transition
                    as={React.Fragment}
                    show={deleteConfirmOpen}
                    enter="transition-all duration-150"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition-all duration-150"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95">
                    <div className="absolute right-0 top-full z-50 mt-2 flex w-72 flex-col gap-2 rounded-xl bg-secondary-50 p-4 shadow-lg">
                      <h4 className="font-semibold">{t('deleteTitle')}</h4>
                      <p className="text-sm text-secondary-600">
                        {t('deleteDescription', { listName: card.name })}
                      </p>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="tertiary"
                          onClick={() => setDeleteConfirmOpen(false)}
                          className="rounded-xl">
                          {t('cancelButton')}
                        </Button>
                        <Button variant="danger-v2" onClick={handleDelete} className="rounded-xl">
                          {t('deleteButton')}
                        </Button>
                      </div>
                    </div>
                  </Transition>
                </div>
              </>
            ) : (
              <>
                <Button
                  variant="tertiary"
                  onClick={() => {
                    setPendingScores({});
                    setCancelVersion((v) => v + 1);
                    setEditMode(false);
                  }}
                  className="rounded-xl">
                  {t('cancelButton')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleSave}
                  loading={saving}
                  className="rounded-xl text-secondary-50">
                  {t('forms.saveButton')}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 p-3">
          <div className="hidden grid-cols-[1fr_auto_auto] gap-4 px-3 text-xs font-semibold uppercase tracking-wide text-gray-500 md:grid md:grid-cols-[1fr_8rem_8rem]">
            <span>{t('name')}</span>
            <span className="text-center">{t('score')}</span>
            <span className="text-right">{t('lastUpdated')}</span>
          </div>
          {sortedScores.map((s, index) => (
            <ScoreRow
              key={`${card.scoreCardId}_${s.memberId}`}
              card={card}
              scoreData={s}
              index={index}
              editMode={editMode}
              cancelVersion={cancelVersion}
              onScoreChange={(memberId, newScore) =>
                setPendingScores((prev) => ({ ...prev, [memberId]: newScore }))
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PodiumPlace({
  score,
  place,
  size,
  isFirst = false,
  card,
}: {
  score: ScoreCardScoreModel | undefined;
  place: number;
  size: 'small' | 'medium' | 'large';
  isFirst?: boolean;
  card: ScoreCard;
}): JSX.Element | null {
  const { t: generalT } = useTranslation('', { keyPrefix: 'general' });
  if (!score) return null;

  const sizeConfig = {
    small: { avatar: 'h-10 w-10 md:h-12 md:w-12', podiumH: 'h-10 md:h-12', podiumW: 'w-12 md:w-16', mt: 'mt-2' },
    medium: { avatar: 'h-12 w-12 md:h-14 md:w-14', podiumH: 'h-12 md:h-14', podiumW: 'w-16 md:w-20', mt: 'mt-1' },
    large: { avatar: 'h-14 w-14 md:h-16 md:w-16', podiumH: 'h-16 md:h-20', podiumW: 'w-20 md:w-24', mt: 'mt-0' },
  };
  const cfg = sizeConfig[size];

  return (
    <div className={`flex flex-col items-center ${cfg.mt}`}>
      <div className="flex flex-col items-center">
        <div className="z-10 -mb-3">
          <Avatar
            src={undefined}
            alias={`${score.firstName || ''} ${score.lastName || ''}`}
            className={classNames(cfg.avatar, 'rounded-full text-xl')}
          />
        </div>
        <div
          className={classNames(
            cfg.podiumW,
            cfg.podiumH,
            'flex items-end justify-center rounded-t-md bg-primary-300',
          )}>
          <span className="mb-1 text-xs font-semibold text-secondary-50">{place}</span>
        </div>
      </div>
      <p className={`mt-1 text-center font-semibold ${isFirst ? 'text-sm md:text-base' : 'text-xs md:text-sm'}`}>
        {score.firstName} {score.lastName}
      </p>
      <p className="text-xs text-secondary-600">{formatScore(score.score, card.type, generalT)}</p>
    </div>
  );
}

function ScoreRow({
  card,
  scoreData,
  index,
  editMode,
  cancelVersion,
  onScoreChange,
}: {
  card: ScoreCard;
  scoreData: ScoreCardScoreModel;
  index: number;
  editMode?: boolean;
  cancelVersion?: number;
  onScoreChange?: (memberId: number, newScore: number) => void;
}): JSX.Element {
  const { t: generalT } = useTranslation('', { keyPrefix: 'general' });
  const [score, setScore] = useState<number>(scoreData.score);
  // For time and other string-based inputs we keep a raw string while the user types
  const [rawInput, setRawInput] = useState<string>(() => formatScore(scoreData.score, card.type, generalT));

  const isTime = card.type === ScoreCardType.TIME_HH_MM ||
    card.type === ScoreCardType.TIME_MM_SS ||
    card.type === ScoreCardType.TIME_HH_MM_SS;
  const isBoolean = card.type === ScoreCardType.BOOLEAN;
  const isGrade = card.type === ScoreCardType.GRADE;
  const isPercentage = card.type === ScoreCardType.PERCENTAGE;

  useEffect(() => {
    setScore(scoreData.score);
    setRawInput(formatScore(scoreData.score, card.type, generalT));
  }, [scoreData.score, cancelVersion]);

  const commitValue = (numeric: number) => {
    const clamped = clampScore(numeric, card.type);
    setScore(clamped);
    setRawInput(formatScore(clamped, card.type, generalT));
    onScoreChange?.(scoreData.memberId, clamped);
  };

  const renderEditControl = () => {
    if (isBoolean) {
      return (
        <div className="flex items-center justify-center gap-2">
          {[0, 1].map((val) => (
            <button
              key={val}
              type="button"
              onClick={() => commitValue(val)}
              className={classNames(
                'rounded-lg px-3 py-1 text-sm font-medium transition-colors',
                score === val
                  ? 'bg-primary-300 text-secondary-50'
                  : 'bg-secondary-200 text-secondary-900 hover:bg-secondary-300',
              )}>
              {val === 1 ? generalT('yes') : generalT('no')}
            </button>
          ))}
        </div>
      );
    }

    if (isTime) {
      const placeholder =
        card.type === ScoreCardType.TIME_HH_MM_SS ? 'HH:MM:SS' :
        card.type === ScoreCardType.TIME_HH_MM ? 'HH:MM' : 'MM:SS';
      return (
        <input
          type="text"
          className="h-8 w-28 rounded-xl bg-secondary-200 bg-opacity-60 text-center text-sm"
          value={rawInput}
          placeholder={placeholder}
          onChange={(ev) => setRawInput(ev.target.value)}
          onBlur={() => {
            const numeric = parseScore(rawInput, card.type);
            commitValue(numeric);
          }}
        />
      );
    }

    // COUNT, GRADE, PERCENTAGE — numeric with ±1 buttons
    const step = isGrade ? 0.5 : 1;
    return (
      <div className="flex items-center justify-center gap-1">
        <button
          type="button"
          onClick={() => commitValue(score - step)}
          className="flex-shrink-0 rounded-full bg-danger p-1 text-sm text-secondary-50">
          <FaMinus />
        </button>
        <input
          type="number"
          step={step}
          min={isGrade ? 0 : isPercentage ? 0 : undefined}
          max={isGrade ? 10 : isPercentage ? 100 : undefined}
          className="no-spinners h-8 w-16 rounded-xl bg-secondary-200 bg-opacity-60 text-center text-sm"
          value={isGrade ? score : Math.round(score)}
          onChange={(ev) => {
            const v = parseScore(ev.target.value, card.type);
            setScore(v);
            onScoreChange?.(scoreData.memberId, v);
          }}
          onBlur={(ev) => {
            const v = parseScore(ev.target.value, card.type);
            commitValue(v);
          }}
        />
        <button
          type="button"
          onClick={() => commitValue(score + step)}
          className="flex-shrink-0 rounded-full bg-primary-300 p-1 text-sm text-secondary-50">
          <FaPlus />
        </button>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 items-center gap-3 rounded-[8px] bg-secondary-200/70 px-3 py-3 md:grid-cols-[1fr_8rem_8rem] md:gap-4">
      <div className="flex min-w-0 items-center gap-2">
        <span className="hidden w-6 flex-shrink-0 text-sm text-gray-500 md:inline-flex">
          {index + 1}
        </span>
        <Avatar
          src={undefined}
          alias={`${scoreData.firstName || ''} ${scoreData.lastName || ''}`}
          className="h-8 w-8 flex-shrink-0 rounded-full text-sm"
        />
        <span className="block truncate text-sm font-semibold text-primary-900">
          {scoreData.firstName} {scoreData.lastName}
        </span>
      </div>
      <div className="flex items-center justify-start md:justify-center">
        {editMode ? (
          renderEditControl()
        ) : (
          <span className="font-medium text-primary-900">
            {formatScore(scoreData.score, card.type, generalT)}
          </span>
        )}
      </div>
      <div className="hidden text-right text-sm text-gray-500 md:block">
        {format(new Date(scoreData.lastUpdated), 'd MMM')}
      </div>
    </div>
  );
}

/** Clamps a score to the valid range for its type. */
function clampScore(value: number, type: ScoreCardType): number {
  switch (type) {
    case ScoreCardType.GRADE:
      return Math.min(10, Math.max(0, value));
    case ScoreCardType.PERCENTAGE:
      return Math.min(100, Math.max(0, Math.round(value)));
    case ScoreCardType.BOOLEAN:
      return value >= 1 ? 1 : 0;
    case ScoreCardType.COUNT:
      return Math.round(value);
    case ScoreCardType.TIME_HH_MM:
    case ScoreCardType.TIME_MM_SS:
    case ScoreCardType.TIME_HH_MM_SS:
      return Math.max(0, Math.floor(value));
    default:
      return value;
  }
}

function SelectCardPrompt(): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.lists.score' });
  return (
    <section className="flex h-full flex-col items-center justify-center py-12 opacity-75">
      <img src="/assets/figures/mess.svg" alt="select card" className="w-1/3 max-w-xs" />
      <h3 className="mt-4 text-center text-2xl">{t('selectCardTitle')}</h3>
    </section>
  );
}

function ScoreCardEditForm({
  onEdit,
  setEditModalOpen,
  setCard,
  initialState,
  membersOfCommunity,
}: {
  onEdit: (submittedCard: { name: string; ascending: boolean; description?: string }) => Promise<void>;
  setEditModalOpen: Dispatch<SetStateAction<boolean>>;
  setCard: (card: ScoreCard) => void;
  initialState: ScoreCard;
  membersOfCommunity: ConnectionsListConnection[];
}): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.lists.score' });
  const { t: errorT } = useTranslation('', { keyPrefix: 'page.lists.score.error' });
  const [name, setName] = useState(initialState.name);
  const [description, setDescription] = useState(initialState.description ?? '');
  const [ascending, setAscending] = useState(initialState.ascending ?? false);
  const [editToggle, setEditToggle] = useState(false);
  const [selectedMembersIdsList, setSelectedMembersIdsList] = React.useState<number[]>(
    membersOfCommunity
      .map((member) => member.id)
      .filter((id) => (initialState.scores || []).some((score) => score.memberId === id)),
  );

  return (
    <>
      <Labeled label={t('forms.name')}>
        <input
          value={name}
          onChange={(ev) => setName(ev.target.value)}
          type="text"
          placeholder={t('forms.namePlaceholder')}
        />
      </Labeled>
      <Labeled label={t('forms.description')}>
        <textarea
          value={description}
          onChange={(ev) => setDescription(ev.target.value)}
          placeholder={t('forms.descriptionPlaceholder')}
          rows={3}
        />
      </Labeled>
      <p className="font-medium">{t('forms.ascending')}</p>
      <TabButtons
        initialState={ascending}
        containerClassName="bg-secondary-200"
        highlightClassName="bg-secondary-50"
        buttonClassName="px-2"
        buttonSelectedClassName="px-2"
        options={[
          { label: t('forms.highest'), value: false as const },
          { label: t('forms.lowest'), value: true as const },
        ]}
        onTabChange={setAscending}
      />
      <p className="font-medium">{t('forms.typeCurrent')}</p>
      <p className="text-base text-secondary-600">
        {initialState.type ? t(`forms.type.${initialState.type.toLowerCase()}`) : t('type.count')}
      </p>
      <p className="text-sm italic text-secondary-600">{t('forms.typeCannotChange')}</p>
      <div className="flex items-center gap-2">
        <Toggle state={editToggle} handleToggle={() => setEditToggle((v) => !v)} />
        <label>{t('forms.changeMembersMode')}</label>
      </div>
      {editToggle && (
        <ConnectionsList
          connections={membersOfCommunity}
          selected={selectedMembersIdsList}
          hasSearch={false}
          setSelected={setSelectedMembersIdsList}
          handleSelectFunction={async (id: number) => {
            const res = await addMemberToScoreCard(initialState.scoreCardId, id).catch((err: Error) => {
              toast.error(err.message === 'UNAUTHORIZED' ? errorT('unauthorizedError') : errorT('addMemberError'));
              return null;
            });
            if (res) setCard(res);
            return !!res;
          }}
          handleDiselectFunction={async (id: number) => {
            const res = await removeMemberFromScoreCard(initialState.scoreCardId, id).catch((err: Error) => {
              toast.error(err.message === 'UNAUTHORIZED' ? errorT('unauthorizedError') : errorT('removeMemberError'));
              return null;
            });
            if (res) setCard(res);
            return !!res;
          }}
        />
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          onClick={() => setEditModalOpen(false)}
          className="rounded-xl"
          variant="danger-v2">
          {t('cancelButton')}
        </Button>
        <Button
          onClick={() => {
            onEdit({ name, ascending, description })
              .then(() => setEditModalOpen(false))
              .catch((err: Error) => {
                if (err.message === 'STOP') toast.error(errorT('emptyNameError'));
              });
          }}
          className="rounded-xl text-secondary-50"
          variant="secondary">
          {t('forms.saveButton')}
        </Button>
      </div>
    </>
  );
}
